import { useCallback } from 'react';
import useWfWorkplaceRepo, {
  WfWorkplacePaginatedSearchParameters,
  WfWorkplaceSearchParameters,
  WfWorkplaceReservationParameters,
} from '../repository/WfWorkplaceRepo';
import { useWfWorkplaceStore } from '../stores/WfWorkplaceStore';
import {
  useWfFilterStore,
  WfBranchOption,
  WfReservedOptions,
  WfSalesOfficeOption,
  WfSectionOption,
  WfSquadOption,
} from '../stores/WfFilterStore';
import { useWfUserStore } from '../stores/WfUserStore';
import { WfPaginatedWorkplace } from '../models/WfWorkplace';
import useWfOptionsRepo, {
  WfOrganizationBranchOption,
  WfOrganizationOptions,
  WfOrganizationSalesOfficeOption,
  WfOrganizationSectionOption,
  WfOrganizationSquadOption,
} from '../repository/WfOptionsRepo';
import { buildDownloadFilename } from '../repository/common/buildDownloadFilename';
import { prepareWfWorkplaceCSVRequestBody } from './helper/wfWorkplaceCSVRequestBody';
import { useTranslation } from 'react-i18next';
import { operationStatusCodes } from '../models/enums/WfOperationtStatus';
import { useWfTyphoonStore } from '../stores/WfTyphoonStore';
import { mapSingleItemToBusinessSituation } from 'src/ui/wf/desktop/summary/businessSummary/BusinessSummaryColumns';
import { mapSingleBranchToSituation } from 'src/ui/wf/desktop/summary/branchSummary/BranchSummaryColumns';
import { calculateTotalSummary } from './helper/calculateTotalBranchSummary';
import { calculateTotalSalesSummary } from './helper/calculateTotalSalesSummary';
import { WfBranchSummaryResponse, WfSalesOfficeSummaryResponse } from '../models/WfSummary';

export interface WorkplaceReservationCreateInitialParams {
  branchCode: string;
  customerId: string;
  engineerUserId: string;
  reserverUserId?: string;
}

export interface WorkplaceReservationCancelInitialParams {
  branchCode: string;
  customerId: string;
  engineerUserId: string;
  reserverUserId: string;
}

type WorkplaceReservationParams =
  | WorkplaceReservationCreateInitialParams
  | WorkplaceReservationCancelInitialParams;

function useWfWorkplaceService() {
  const { t } = useTranslation();
  const { user } = useWfUserStore();
  const {
    setAvailableWorkplaces,
    setWorkplaceSummary,
    setPaginatedWorkplace,
    setSelectedWorkplace,
  } = useWfWorkplaceStore();
  const {
    filterParameters,
    filterPagingParameters,
    setBranchOptions,
    setSalesOfficeOptions,
    setSectionOptions,
    setSquadOptions,
    setStatusOptions,
    setUserOffice,
  } = useWfFilterStore();

  const { currentTyphoon } = useWfTyphoonStore();

  const {
    postWorkplaceSearchParametersRequest,
    postWorkplacePaginatedParametersRequest,
    postCreateWorkplaceReservationRequest,
    postCancelWorkplaceReservationRequest,
    getWorkplaceSummaryRequest,
    getWfWorkplaceCSV,
    getSummaryByBranch,
    getSummaryBySalesOffice,
    getDisasterAreaSummaryByBranch,
    getDisasterAreaSummaryBySalesOffice,
  } = useWfWorkplaceRepo();

  const { getOrganizationOptionsRequest, getStatusesOptionsRequest } = useWfOptionsRepo();

  const toBranchOption = ({ code, name }: WfOrganizationBranchOption): WfBranchOption => ({
    code,
    name,
  });

  const toSalesOfficeOption = ({
    code,
    name,
    location,
    defaultScale,
    searchRange,
  }: WfOrganizationSalesOfficeOption): WfSalesOfficeOption => ({
    code,
    name,
    ...(location !== undefined && { location }),
    ...(defaultScale !== undefined && { defaultScale }),
    ...(searchRange !== undefined && { searchRange }),
  });

  const toSectionOption = ({
    code,
    name,
    location,
    defaultScale,
    searchRange,
  }: WfOrganizationSectionOption): WfSectionOption => ({
    code,
    name,
    ...(location !== undefined && { location }),
    ...(defaultScale !== undefined && { defaultScale }),
    ...(searchRange !== undefined && { searchRange }),
  });

  const toSquadOption = ({ code, name }: WfOrganizationSquadOption): WfSquadOption => ({
    code,
    name,
  });

  const fetchSearchOptions = useCallback(async () => {
    const options = await getOrganizationOptionsRequest();

    const populateHierarchyFromResponse = (
      response: WfOrganizationOptions,
      selectedBranchCode?: string,
      selectedSalesOfficeCode?: string,
      selectedSectionCode?: string
    ) => {
      const branches = response.branchOffices ?? [];
      setBranchOptions(branches.map(toBranchOption));

      const resolvedBranch = branches.find((branch) => branch.code === selectedBranchCode) ?? null;

      const salesOffices = resolvedBranch?.salesOffices ?? [];
      setSalesOfficeOptions(salesOffices.map(toSalesOfficeOption));

      const resolvedSalesOffice =
        salesOffices.find((office) => office.code === selectedSalesOfficeCode) ?? null;

      const sections = resolvedSalesOffice?.sections ?? [];
      setSectionOptions(sections.map(toSectionOption));

      const resolvedSection =
        sections.find((section) => section.code === selectedSectionCode) ?? null;

      const squads = resolvedSection?.squads ?? [];
      setSquadOptions(squads.map(toSquadOption));
    };

    populateHierarchyFromResponse(
      options,
      filterParameters.branch,
      filterParameters.salesOffice,
      filterParameters.section
    );
  }, [
    filterParameters.branch,
    filterParameters.salesOffice,
    filterParameters.section,
    getOrganizationOptionsRequest,
    setBranchOptions,
    setSalesOfficeOptions,
    setSectionOptions,
    setSquadOptions,
  ]);

  const fetchBranchOptions = useCallback(async () => {
    const options = await getOrganizationOptionsRequest();

    const branches = options.branchOffices ?? [];
    setBranchOptions(branches.map(toBranchOption));
  }, [getOrganizationOptionsRequest, setBranchOptions]);

  const fetchSalesOfficeOptions = useCallback(
    async (branchCode: string) => {
      const options = await getOrganizationOptionsRequest(branchCode);

      const branch = options.branchOffices?.find(
        (optionBranch: WfOrganizationBranchOption) => optionBranch.code === branchCode
      );

      const salesOffices = branch?.salesOffices ?? [];
      const formattedOptions = salesOffices.map(toSalesOfficeOption);

      setSalesOfficeOptions(formattedOptions);

      return formattedOptions;
    },
    [getOrganizationOptionsRequest, setSalesOfficeOptions]
  );

  const fetchSectionOptions = useCallback(
    async (branchCode: string, salesOfficeCode: string) => {
      await getOrganizationOptionsRequest(branchCode, salesOfficeCode).then((options) => {
        const branch = options.branchOffices?.find(
          (optionBranch: WfOrganizationBranchOption) => optionBranch.code === branchCode
        );

        const salesOffice = branch?.salesOffices.find(
          (optionSalesOffice: WfOrganizationSalesOfficeOption) =>
            optionSalesOffice.code === salesOfficeCode
        );

        const sections = salesOffice?.sections ?? [];
        setSectionOptions(sections.map(toSectionOption));
      });
    },
    [getOrganizationOptionsRequest, setSectionOptions]
  );

  const fetchSquadOptions = useCallback(
    async (branchCode: string, salesOfficeCode: string, sectionCode: string) => {
      await getOrganizationOptionsRequest(branchCode, salesOfficeCode, sectionCode).then(
        (options) => {
          const branch = options.branchOffices?.find(
            (optionBranch: WfOrganizationBranchOption) => optionBranch.code === branchCode
          );

          const salesOffice = branch?.salesOffices.find(
            (optionSalesOffice: WfOrganizationSalesOfficeOption) =>
              optionSalesOffice.code === salesOfficeCode
          );

          const section = salesOffice?.sections.find(
            (optionSection: WfOrganizationSectionOption) => optionSection.code === sectionCode
          );

          const squads = section?.squads ?? [];
          setSquadOptions(squads.map(toSquadOption));
        }
      );
    },
    [getOrganizationOptionsRequest, setSquadOptions]
  );

  const fetchSalesOffice = useCallback(
    async (branchCode: string, salesOfficeCode: string | undefined) => {
      if (salesOfficeCode === undefined) return undefined;

      let branch: WfOrganizationBranchOption | undefined;
      if (branchCode === '30' && salesOfficeCode === '306680') {
        const okinichiBranchCode = '  '; // For Okinichi user, need to adjust branch code
        const options = await getOrganizationOptionsRequest(okinichiBranchCode);

        branch = options.branchOffices?.find(
          (option: WfOrganizationBranchOption) => option.code === okinichiBranchCode
        );
      } else {
        const options = await getOrganizationOptionsRequest(branchCode);

        branch = options.branchOffices?.find(
          (option: WfOrganizationBranchOption) => option.code === branchCode
        );
      }

      const salesOffices = branch?.salesOffices ?? [];
      return salesOffices.find((option) => option.code === salesOfficeCode);
    },
    [getOrganizationOptionsRequest]
  );

  const fetchWorkplaces = useCallback(async () => {
    if (!currentTyphoon) return;

    const hasCustomerSearch = Boolean(filterParameters.customerNumber?.trim());

    // When a customer number is provided, exclude FE/site search parameters (sales office, section, squad, status, reservation status) as they are not applicable to customer-based searches
    const paramsToUse: WfWorkplaceSearchParameters = {
      ...(filterParameters.branch && { branch: filterParameters.branch }),
      disasterId: currentTyphoon.id,
      ...(hasCustomerSearch && { customerNumber: filterParameters.customerNumber }),
      ...(!hasCustomerSearch && {
        statusList: operationStatusCodes(filterParameters.statusList),
      }),
      ...(!hasCustomerSearch &&
        filterParameters.reserved !== undefined && { reserved: filterParameters.reserved }),
      ...(!hasCustomerSearch &&
        filterParameters.salesOffice && { salesOffice: filterParameters.salesOffice }),
      ...(!hasCustomerSearch && filterParameters.section && { section: filterParameters.section }),
      ...(!hasCustomerSearch && filterParameters.squad && { squad: filterParameters.squad }),
    };

    await postWorkplaceSearchParametersRequest(paramsToUse).then((workplaces) =>
      setAvailableWorkplaces(workplaces)
    );
  }, [
    filterParameters,
    postWorkplaceSearchParametersRequest,
    currentTyphoon,
    setAvailableWorkplaces,
  ]);

  const fetchPaginatedWorkplaces = useCallback(
    async (newPageNumber?: number) => {
      if (!currentTyphoon) return;

      const hasCustomerSearch = Boolean(filterParameters.customerNumber?.trim());

      // When a customer number is provided, exclude FE/site search parameters (sales office, section, squad, status, reservation status) as they are not applicable to customer-based searches
      const searchParams: WfWorkplaceSearchParameters = {
        ...(filterParameters.branch && { branch: filterParameters.branch }),
        disasterId: currentTyphoon.id,
        ...(hasCustomerSearch && { customerNumber: filterParameters.customerNumber }),
        ...(!hasCustomerSearch && {
          statusList: operationStatusCodes(filterParameters.statusList),
        }),
        ...(!hasCustomerSearch &&
          filterParameters.reserved !== undefined && { reserved: filterParameters.reserved }),
        ...(!hasCustomerSearch &&
          filterParameters.salesOffice && { salesOffice: filterParameters.salesOffice }),
        ...(!hasCustomerSearch &&
          filterParameters.section && { section: filterParameters.section }),
        ...(!hasCustomerSearch && filterParameters.squad && { squad: filterParameters.squad }),
      };

      const paramsToUse: WfWorkplacePaginatedSearchParameters = {
        searchParams,
        pageSize: filterPagingParameters.pageSize,
        pageNumber: newPageNumber ?? filterPagingParameters.pageNumber,
      };

      const workplaces = (await postWorkplacePaginatedParametersRequest(
        paramsToUse
      )) as WfPaginatedWorkplace;
      setPaginatedWorkplace(workplaces);
    },
    [
      currentTyphoon,
      filterPagingParameters,
      filterParameters,
      postWorkplacePaginatedParametersRequest,
      setPaginatedWorkplace,
    ]
  );

  const fetchStatusOptions = useCallback(async () => {
    await getStatusesOptionsRequest().then((options) => setStatusOptions(options));
  }, [getStatusesOptionsRequest, setStatusOptions]);

  const buildReservationRequestParams = (
    engineerUserId: string,
    branchCode: string,
    customerId: string,
    reserverUserId?: string
  ): WfWorkplaceReservationParameters | undefined => {
    if (!user || !currentTyphoon || !engineerUserId) return;

    return {
      engineerUserId,
      reserverUserId: reserverUserId ? reserverUserId : user.id,
      disasterId: currentTyphoon.id,
      branchCode,
      customerId,
    };
  };

  const validateReservation = (reservation: WorkplaceReservationParams | null) => {
    if (!reservation || !reservation.engineerUserId) {
      return { validReservation: null };
    }

    const validReservation = buildReservationRequestParams(
      reservation.engineerUserId,
      reservation.branchCode,
      reservation.customerId,
      reservation.reserverUserId
    );

    return { validReservation };
  };

  const createWorkplaceReservation = async (
    reservation: WorkplaceReservationCreateInitialParams
  ) => {
    const { validReservation } = validateReservation(reservation);

    if (!validReservation) return;

    try {
      const response = await postCreateWorkplaceReservationRequest({
        engineerUserId: validReservation.engineerUserId,
        reserverUserId: validReservation.reserverUserId,
        disasterId: validReservation.disasterId,
        branchCode: validReservation.branchCode,
        customerId: validReservation.customerId,
      });

      return response;
    } catch (error) {
      console.log(error);
      console.error('Error creating workplace reservation:', error);
      throw error;
    } finally {
      fetchWorkplaces().catch((refreshError) => console.log(refreshError));
      fetchPaginatedWorkplaces().catch((refreshError) => console.log(refreshError));
    }
  };

  const cancelWorkplaceReservation = async (
    reservation: WorkplaceReservationCancelInitialParams
  ) => {
    const { validReservation } = validateReservation(reservation);

    if (!validReservation) return;

    try {
      const response = await postCancelWorkplaceReservationRequest(validReservation);

      return response;
    } catch (error) {
      console.error('Error canceling workplace reservation:', error);
      throw error;
    } finally {
      fetchWorkplaces().catch((refreshError) => console.log(refreshError));
      fetchPaginatedWorkplaces().catch((refreshError) => console.log(refreshError));
    }
  };

  const getWorkplaceSummary = useCallback(async () => {
    await getWorkplaceSummaryRequest().then((dataSummary) => {
      if (dataSummary) {
        setWorkplaceSummary([dataSummary]);
      }
    });
  }, [getWorkplaceSummaryRequest, setWorkplaceSummary]);

  const prepareWfWorkplaceCSVDownload = useCallback(async () => {
    if (!currentTyphoon) return;

    const { branch, salesOffice, section, squad, customerNumber, statusList, reserved } =
      filterParameters;

    if (!branch) return;

    const hasCustomerSearch = Boolean(customerNumber?.trim());

    // お客さま番号が指定されている場合は FE/現場検索の組織条件（営業所・係・班）を除外
    // お客さま番号検索時は statusList・reserved を無効化してバックエンド側でフィルタをかけない
    const params = {
      branch,
      statusList: hasCustomerSearch ? [] : operationStatusCodes(statusList),
      reserved: hasCustomerSearch ? WfReservedOptions.all : (reserved ?? WfReservedOptions.all),
      disasterId: currentTyphoon.id,
      ...(customerNumber && { customerNumber }),
      ...(!hasCustomerSearch && salesOffice && { salesOffice }),
      ...(!hasCustomerSearch && section && { section }),
      ...(!hasCustomerSearch && squad && { squad }),
    };

    const body = prepareWfWorkplaceCSVRequestBody(t, params);

    const blob = await getWfWorkplaceCSV(body);

    return { blob, filename: buildDownloadFilename('L') };
  }, [currentTyphoon, filterParameters, getWfWorkplaceCSV, t]);

  const fetchSelectedWorkplace = useCallback(
    async (branchCode: string, customerId: string, disasterId: string) => {
      try {
        const workplaces = await postWorkplaceSearchParametersRequest({
          branch: branchCode,
          customerNumber: customerId,
          disasterId,
        });
        if (workplaces.length > 0) {
          setSelectedWorkplace(workplaces[0]);
        }
      } catch (error) {
        console.log(error);
      }
    },
    [postWorkplaceSearchParametersRequest, setSelectedWorkplace]
  );

  const fetchUserSalesOffice = useCallback(
    async (userBranchCode: string, userSalesOfficeCode: string) => {
      try {
        const options = await getOrganizationOptionsRequest(userBranchCode);
        const branch = options.branchOffices?.find(
          (b: WfOrganizationBranchOption) => b.code === userBranchCode
        );
        const salesOffices = (branch?.salesOffices ?? []).map(toSalesOfficeOption);

        if (salesOffices.length) {
          const foundOffice = salesOffices.find((office) => office.code === userSalesOfficeCode);
          if (foundOffice) {
            setUserOffice(foundOffice);
          }
        }
      } catch (error) {
        console.error('Failed to find user sales office:', error);
      }
    },
    [getOrganizationOptionsRequest, setUserOffice]
  );

  const fetchSummaryByBranch = useCallback(
    async (disasterId: string, branchCode?: string): Promise<WfBranchSummaryResponse[]> => {
      const data = await getSummaryByBranch({
        disasterId,
        ...(branchCode !== undefined && { branchCode }),
      });

      if (!data || !Array.isArray(data) || data.length === 0) return [];

      try {
        if (!branchCode) {
          const totalOverall = calculateTotalSummary(data, t('wf.summary.all'));
          const firstRow = mapSingleBranchToSituation(totalOverall);

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const branchGroups = data.reduce((acc: Record<string, any[]>, curr) => {
            const code = curr.branchCode || 'unknown';
            if (!acc[code]) acc[code] = [];
            acc[code].push(curr);
            return acc;
          }, {});

          const branchSummaryRows = Object.entries(branchGroups)
            // calculateTotalSummary always clears branchCode, so sort by the original group key
            .sort(([codeA], [codeB]) => codeA.localeCompare(codeB))
            .map(([, group]) => {
              const summary = calculateTotalSummary(
                group,
                group[0].branchName || group[0].branchCode
              );
              return mapSingleBranchToSituation(summary);
            });

          return [firstRow, ...branchSummaryRows];
        }

        const branchTotalData = calculateTotalSummary(data, data[0].branchName);
        const firstRow = mapSingleBranchToSituation(branchTotalData);

        const formattedRows = data.map((item) => mapSingleBranchToSituation(item));

        return [firstRow, ...formattedRows];
      } catch (e) {
        console.error('Error in fetchSummaryByBranch:', e);
        return [];
      }
    },
    [getSummaryByBranch, t]
  );

  const fetchDisasterAreaBranches = useCallback(
    async (disasterId: string): Promise<string[]> => {
      try {
        const data = await getDisasterAreaSummaryByBranch(disasterId);

        if (!data || !Array.isArray(data)) {
          return [];
        }

        return data.map((item) => item.branchCode).filter((code): code is string => !!code);
      } catch (e) {
        console.error('Error mapping disaster area branches:', e);
        return [];
      }
    },
    [getDisasterAreaSummaryByBranch]
  );

  const fetchSummaryBySalesOffice = useCallback(
    async (
      disasterId: string,
      branchCode: string,
      salesOfficeCode?: string
    ): Promise<WfSalesOfficeSummaryResponse[]> => {
      const data = await getSummaryBySalesOffice({
        disasterId,
        branchCode,
        salesOfficeCode: salesOfficeCode || '',
      });

      if (!data || !Array.isArray(data) || data.length === 0) return [];

      try {
        let firstRowName = t('wards.common.total');
        if (salesOfficeCode) {
          firstRowName = data[0]!.salesOfficeName || salesOfficeCode;
        }

        const totalDataRaw = calculateTotalSalesSummary(data, firstRowName);
        const firstRow = mapSingleItemToBusinessSituation(totalDataRaw);

        const otherRows = data
          .map((item) => mapSingleItemToBusinessSituation(item))
          .sort((a, b) => a.sectionCode.localeCompare(b.sectionCode));

        return [firstRow, ...otherRows];
      } catch (e) {
        console.error('Mapping Error in SalesOffice Service:', e);
        return [];
      }
    },
    [getSummaryBySalesOffice, t]
  );

  const fetchDisasterAreaSalesOffices = useCallback(
    async (disasterId: string, branchCode: string): Promise<string[]> => {
      const data = await getDisasterAreaSummaryBySalesOffice(disasterId, branchCode);

      if (!data || !Array.isArray(data)) return [];

      return data;
    },
    [getDisasterAreaSummaryBySalesOffice]
  );

  return {
    fetchWorkplaces,
    fetchSelectedWorkplace,
    fetchBranchOptions,
    fetchSearchOptions,
    fetchSalesOfficeOptions,
    fetchSectionOptions,
    fetchSquadOptions,
    fetchSalesOffice,
    fetchStatusOptions,
    fetchPaginatedWorkplaces,
    createWorkplaceReservation,
    cancelWorkplaceReservation,
    getWorkplaceSummary,
    prepareWfWorkplaceCSVDownload,
    fetchUserSalesOffice,
    fetchSummaryByBranch,
    fetchSummaryBySalesOffice,
    fetchDisasterAreaBranches,
    fetchDisasterAreaSalesOffices,
  };
}

export default useWfWorkplaceService;
